import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useSelection from '../hooks/useSelection';
import TweetSelectionButton from '../components/TweetSelectionButton';
import SEO from '../components/SEO';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || 'https://epstein-files-12.s3.us-east-2.amazonaws.com/raw/Seventh_Production/IMAGES';

const PageContainer = styled.div`
  padding: 4rem 3rem;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const BackButton = styled.button`
  background: #F0E8D8;
  border: 1px solid #C4B5A0;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: #E8DCC8;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0.65rem 1.25rem;
    font-size: 0.85rem;
  }
`;

const DocumentCard = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 
    inset 0 1px 3px rgba(0, 0, 0, 0.05),
    1px 1px 5px rgba(0, 0, 0, 0.08);
  border-radius: 2px;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 0;
  }
`;

const ImageContainer = styled.div`
  margin-bottom: 2rem;
  background: #F9F5EB;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px dashed #D4CFC4;
`;

const DocumentImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border: 1px solid #C4B5A0;
  margin-bottom: 1rem;
  display: block;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FilePath = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.ink};
  font-weight: bold;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #C4B5A0;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const Content = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: bold;
  color: #8A7E70;
  display: block;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #8A7E70;
  font-family: 'Courier New', monospace;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  background: #F0E8D8;
  border: 1.5px solid #D4847C;
  padding: 1.5rem;
  color: #8A3A2A;
  font-family: 'Courier New', monospace;
  border-radius: 2px;
  word-wrap: break-word;
`;

function DocumentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const documentRef = useRef(null);
  const { selection, selectionRect, clearSelection } = useSelection();
  const selectionText = selection?.text?.trim() || '';

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/document/${slug}`);
        
        if (!response.ok) {
          throw new Error('Document not found');
        }
        
        const data = await response.json();
        setDocument(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]); // Only re-run when slug changes

  const isSelectionWithinDocument = useMemo(() => {
    if (!selectionText || !documentRef.current) {
      return false;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    const windowSelection = window.getSelection();
    if (!windowSelection || windowSelection.rangeCount === 0) {
      return false;
    }

    const range = windowSelection.getRangeAt(0);
    if (!range) {
      return false;
    }

    let container = range.commonAncestorContainer;
    while (container && container.nodeType === 3) {
      container = container.parentNode;
    }

    if (!container) {
      return false;
    }

    return documentRef.current.contains(container);
  }, [selectionText]);

  const showTweetButton = !!selectionText && isSelectionWithinDocument;

  const handleTweetSelection = useCallback(() => {
    if (!selectionText || !document) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const normalizedText = selectionText.replace(/\s+/g, ' ').trim();
    const maxQuoteLength = 240;
    const quote =
      normalizedText.length > maxQuoteLength
        ? `${normalizedText.slice(0, maxQuoteLength - 1)}…`
        : normalizedText;

    const tweetText = `"${quote}"`;
    const tweetUrl = new URL('https://twitter.com/intent/tweet');
    tweetUrl.searchParams.set('text', tweetText);

    const createSlug = (path) => {
      const withoutExt = path.substring(0, path.lastIndexOf('.')) || path;
      return withoutExt.replace(/\//g, '-');
    };

    const docSlug = createSlug(document.file_path);
    tweetUrl.searchParams.set('url', `${window.location.origin}/document/${docSlug}`);

    window.open(tweetUrl.toString(), '_blank', 'noopener,noreferrer');
    clearSelection();
  }, [selectionText, document, clearSelection]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading document...</LoadingMessage>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate('/')}>← Back to Search</BackButton>
        <ErrorMessage>
          <strong>Error:</strong> {error}
        </ErrorMessage>
      </PageContainer>
    );
  }

  // Construct image URL
  const cleanBase = S3_BASE_URL.replace(/\/$/, '');
  const cleanPath = document.file_path.replace(/^\//, '');
  const imageUrl = `${cleanBase}/${cleanPath}`;

  return (
    <PageContainer>
      <SEO 
        title={`Document: ${document.file_path}`}
        description={document.summary || `Read the full text of ${document.file_path} from the Epstein Archives.`}
        image={imageUrl}
        type="article"
      />
      <BackButton onClick={() => navigate('/')}>← Back to Search</BackButton>
      
      <DocumentCard ref={documentRef} data-file-path={document.file_path}>
        <FilePath>{document.file_path}</FilePath>
        
        <ImageContainer>
          <a href={imageUrl} target="_blank" rel="noopener noreferrer">
            <DocumentImage 
              src={imageUrl} 
              alt={`Document: ${document.file_path}`}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.style.display = 'none';
              }}
            />
          </a>
        </ImageContainer>
        
        <Content>
          {document.summary && (
            <Section>
              <Label>Summary</Label>
              {document.summary}
            </Section>
          )}
          
          {document.people && (
            <Section>
              <Label>People</Label>
              {document.people}
            </Section>
          )}
          
          {document.locations && (
            <Section>
              <Label>Locations</Label>
              {document.locations}
            </Section>
          )}
          
          {document.dates && (
            <Section>
              <Label>Dates</Label>
              {document.dates}
            </Section>
          )}
          
          {document.ocr_content && (
            <Section>
              <Label>Document Content</Label>
              {document.ocr_content}
            </Section>
          )}
        </Content>
      </DocumentCard>

      <TweetSelectionButton
        selection={selection}
        selectionRect={selectionRect}
        visible={showTweetButton}
        onTweet={handleTweetSelection}
      />
    </PageContainer>
  );
}

export default DocumentDetail;

